import type { ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { uniqWith } from 'ramda';
import { Apollo } from 'src/gql/generated';
import { groupTypename } from 'src/helpers';

export function useGetGroups(options?: Parameters<typeof Apollo.useGetGroupsQuery>[0]): {
  groups: NonNullable<Apollo.GetGroupsQueryHookResult['data']>['groups'];
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetGroupsQuery(options);
  return { groups: data?.groups ?? [], loading };
}

export function useGetGroup(
  groupId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetGroupQuery>[0]>, 'variables'>
): {
  group: NonNullable<Apollo.GetGroupQueryHookResult['data']>['group'] | null;
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetGroupQuery({ ...options, variables: { id: groupId } });
  return { group: data?.group ?? null, loading };
}

export function useGetGroupUsers(
  groupId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetGroupWithUsersQuery>[0]>, 'variables'>
): {
  groupUsers: NonNullable<Apollo.GetGroupWithUsersQueryHookResult['data']>['group']['users'];
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetGroupWithUsersQuery({
    ...options,
    variables: { id: groupId },
  });
  return { groupUsers: data?.group.users ?? [], loading };
}

export function useCreateGroup(): {
  createGroup: (
    options: Parameters<Apollo.CreateGroupMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateGroupMutation>>;
  loading: boolean;
} {
  const [createGroup, { loading }] = Apollo.useCreateGroupMutation();

  return {
    createGroup: options => {
      return createGroup({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              groups: (existingGroups = []) => {
                if (!data) return existingGroups;
                return [
                  ...existingGroups,
                  cache.writeFragment({
                    data: data.createGroup,
                    fragment: gql`
                      fragment NewGroup on Group {
                        __typename
                        id
                      }
                    `,
                  }),
                ];
              },
            },
          });
        },
      });
    },
    loading,
  };
}

export function useDeleteGroup(): {
  deleteGroup: (
    options: Parameters<Apollo.DeleteGroupMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.DeleteGroupMutation>>;
  loading: boolean;
} {
  const [deleteGroup, { loading }] = Apollo.useDeleteGroupMutation();

  return {
    deleteGroup: options => {
      return deleteGroup({
        ...options,
        update: cache => {
          const groupsKey: keyof Apollo.Query = 'groups';

          cache.modify({
            fields: {
              [groupsKey]: (existingGroups: readonly Reference[] = [], { readField }) => {
                return existingGroups.filter(
                  (ref: Reference) => readField('id', ref) !== options?.variables?.id
                );
              },
            },
          });
        },
      });
    },
    loading,
  };
}

export function useAddUsersToGroup(): {
  addUsersToGroup: (
    groupId: UUID,
    options: Parameters<Apollo.AddUsersToGroupMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.AddUsersToGroupMutation>>;
  loading: boolean;
} {
  const [addUsersToGroup, { loading }] = Apollo.useAddUsersToGroupMutation();

  return {
    addUsersToGroup: (groupId, options) => {
      return addUsersToGroup({
        ...options,
        update: (cache, { data }) => {
          if (!data) return;

          cache.modify({
            fields: {
              users: (refs: readonly Reference[] = [], { readField }) => {
                const newRefs = data.addUsersToGroup.users.flatMap(user => {
                  const ref = cache.writeFragment({
                    data: user,
                    fragment: gql`
                      fragment NewUser on User {
                        __typename
                        id
                      }
                    `,
                  });
                  return ref ? [ref] : [];
                });

                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...refs, ...newRefs]
                );
              },
            },
            id: cache.identify({ __typename: groupTypename, id: groupId }),
          });
        },
      });
    },
    loading,
  };
}

export function useRemoveUsersFromGroup(): {
  loading: boolean;
  removeUsersFromGroup: (
    groupId: UUID,
    options: Parameters<Apollo.RemoveUsersFromGroupMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.RemoveUsersFromGroupMutation>>;
} {
  const [removeUsersFromGroup, { loading }] = Apollo.useRemoveUsersFromGroupMutation();

  return {
    loading,
    removeUsersFromGroup: (groupId, options) => {
      return removeUsersFromGroup({
        ...options,
        update: cache => {
          const usersKey: keyof Apollo.Group = 'users';

          cache.modify({
            fields: {
              [usersKey]: (refs: readonly Reference[] = [], { readField }) => {
                const removedUserIds = new Set(options?.variables?.input.userIds ?? []);

                return refs.filter(ref => {
                  const id = readField<UUID>('id', ref);
                  return id && !removedUserIds.has(id);
                });
              },
            },
            id: cache.identify({ __typename: groupTypename, id: groupId }),
          });
        },
      });
    },
  };
}
